import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Grid, Plus, LayoutGrid, List, ListPlus, Clock, Trash2, Edit3, Filter } from 'lucide-react';
 
// Removed BrandDropdown import; checkboxes are used in Filters popover
import { DateRangePicker } from '../components/common/DateRangeSelector';
import { isAfter, parseISO, isValid, startOfMonth, endOfMonth, subMonths, startOfDay, endOfDay, subDays } from 'date-fns';
import { SocialMediaCard } from '../components/calendar/SocialMediaCard';
// Removed PostStatusDropdown import; checkboxes are used in Filters popover
import type { PostStatus } from '../components/common/PostStatusDropdown';
import { BoardView } from '../components/calendar/BoardView';
import { CreatePostModal } from '../components/posts/CreatePostModal';
import { IdeaModal } from '../components/posts/IdeaModal';
import { useCreatePost } from '../hooks/useCreatePost';
import { PostForm } from '../components/posts/PostForm';
import { SocialIcon } from '../components/icons/SocialIcon';
import { DeleteConfirmationModal } from '../components/common/DeleteConfirmationModal';
import { StatusModal } from '../components/common/StatusModal';
import { ActionsMenu } from '../components/common/ActionsMenu';
import { NoPostsCard } from '../components/common/NoPostsCard';
import { PostPreview } from '../components/calendar/PostPreview';
import { MediaDisplay } from '../components/posts/previews/MediaDisplay';
import { PdfThumb } from '../components/common/PdfThumb';
import { auth } from '../config/firebase';
import { useContentHubUrlState } from '../store/contentHubStore';
import type { Brand } from '../types';
import type { Post } from '../types/calendar';
import { usePosts } from '../hooks/usePosts';
import { useUrlValue } from '../store/urlStateStore';
import { useDeletePost } from '../hooks/useDeletePost';
import { useUpdatePost } from '../hooks/useUpdatePost';
import { useBulkUpdatePosts } from '../hooks/useBulkUpdatePosts';
import { useToast } from '../context/ToastContext';
import { BulkEditModal, type BulkUpdateData } from '../components/modals/BulkEditModal';
import { formatLocaleDate } from '../utils/dateUtils';
import { logger } from '../utils/logger';
import { normalizeMedia } from '../utils/mediaUtils';
import { getPostDeleteErrorMessage } from '../utils/postErrors';
import { GifLoader } from '../components/website/GifLoader';
import { PageLayout } from '../components/layout';
import { getMediaTagVariant, tagStyles, tagEmoji, getTagLabels } from '../utils/tagStyles';

// Utility functions copied from Calendar.tsx
const truncateContent = (content: string, maxLength: number = 70) => {
  if (content.length <= maxLength) return content;
  return content.substring(0, maxLength - 3) + '...';
};

// Helper function to capitalize the first letter of a string
const capitalize = (s: string) => {
  if (typeof s !== 'string' || s.length === 0) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// Helper function to get platforms as array from either array or object formats
const getPlatformsArray = (post: any): string[] => {
  if (!post || !post.platforms) return [];
  
  // If already an array (normalized from API), return as-is
  if (Array.isArray(post.platforms)) return post.platforms as string[];
  
  // If object-shaped (new detailed format), return its keys
  if (typeof post.platforms === 'object') return Object.keys(post.platforms);
  
  return [];
};

// Helper function to get platform-specific status
const getPlatformStatus = (post: any, platform: string): string => {
  // Prefer object-shaped `platforms` if available
  if (post.platforms && !Array.isArray(post.platforms) && typeof post.platforms === 'object') {
    const statusFromPlatforms = post.platforms?.[platform]?.results?.status;
    if (statusFromPlatforms) return statusFromPlatforms;
  }
  
  // Fallback to `platformData` preserved by the data layer
  if (post.platformData && typeof post.platformData === 'object') {
    const statusFromPlatformData = post.platformData?.[platform]?.results?.status;
    if (statusFromPlatformData) return statusFromPlatformData;
  }
  
  // Final fallback to global status
  return post.status;
};

// Helper function to get the appropriate date to display based on post status
const getDisplayDate = (post: Post): string => {
  // For published posts, prioritize publishedAt
  if (post.status === 'published') {
    // First check global publishedAt
    if (post.publishedAt) {
      return post.publishedAt;
    }
    
    // Look for platform-specific publication dates
    // 1) From object-shaped `platforms`
    const platformsObj: any = (post as any).platforms;
    if (platformsObj && !Array.isArray(platformsObj) && typeof platformsObj === 'object') {
      for (const platform in platformsObj) {
        const platformResults = platformsObj[platform]?.results;
        if (platformResults?.publishedAt) {
          return platformResults.publishedAt;
        }
      }
    }
    // 2) From `platformData` fallback
    if ((post as any).platformData && typeof (post as any).platformData === 'object') {
      const platformDataObj: any = (post as any).platformData;
      for (const platform in platformDataObj) {
        const platformResults = platformDataObj[platform]?.results;
        if (platformResults?.publishedAt) {
          return platformResults.publishedAt;
        }
      }
    }
    
    // Fallback to updatedAt for published posts without publication date
    return post.updatedAt || '';
  }
  
  // For scheduled posts, show scheduledAt if available
  if (post.status === 'scheduled' && post.scheduledAt) {
    return post.scheduledAt;
  }
  
  // For all other cases, use updatedAt
  return post.updatedAt || '';
};

// Published On: prefer scheduledAt when present; fallback to publishedAt
const getPublishedOnDate = (post: Post): string => {
  if (post.scheduledAt) return post.scheduledAt;
  if (post.publishedAt) return post.publishedAt;
  return '';
};

// Enhanced status display helper
const getStatusDisplayInfo = (post: Post, currentUserId: string | undefined, t: Function) => {
  const status = post.status as PostStatus; // Assert type

  if (status === 'waiting_for_approval') {
    if (currentUserId && currentUserId === post.reviewerId) {
      // Waiting for CURRENT user's review
      return { 
        name: t('pages.status.needsYourApproval', 'Needs Your Review'), 
        emoji: '🔔', // Bell emoji
        colorClasses: 'bg-orange-200 text-orange-800 border border-orange-400' // Brighter/distinct orange
      };
    } else if (currentUserId && currentUserId === post.userId) {
       // Waiting for SOMEONE ELSE's review (current user is author)
       return { 
        name: t('pages.status.waiting_for_approval', 'Waiting Approval'), 
        emoji: '⏳', 
        colorClasses: 'bg-orange-100 text-orange-700' // Standard orange
      };
    } else {
      // Fallback if IDs don't match (shouldn't happen often)
       return { 
        name: t('pages.status.waiting_for_approval', 'Waiting Approval'), 
        emoji: '⏳', 
        colorClasses: 'bg-orange-100 text-orange-700' 
      };
    }
  }

  // Handle other statuses
  switch (status) {
    case 'draft': 
      return { name: t('pages.status.draft', 'Draft'), emoji: '📝', colorClasses: 'bg-gray-100 text-gray-700' };
    case 'scheduled': 
      return { name: t('pages.status.scheduled', 'Scheduled'), emoji: '📅', colorClasses: 'bg-blue-100 text-blue-700' };
    case 'ready_to_publish':
      return { name: t('pages.status.ready_to_publish', 'Ready to Publish'), emoji: '🚀', colorClasses: 'bg-teal-100 text-teal-700' };
    case 'processing': 
      return { name: t('pages.status.processing', 'Processing'), emoji: '⚙️', colorClasses: 'bg-purple-100 text-purple-700' };
    case 'published': 
      return { name: t('pages.status.published', 'Published'), emoji: '✅', colorClasses: 'bg-green-100 text-green-700' };
    case 'rejected': 
      return { name: t('pages.status.rejected', 'Rejected'), emoji: '❌', colorClasses: 'bg-red-100 text-red-700' };
    case 'error': 
       return { name: t('pages.status.error', 'Error'), emoji: '⚠️', colorClasses: 'bg-red-100 text-red-700' };
     // Remove pending/waiting if not used by API
    // case 'pending': 
    //   return { name: t('status.pending', 'Pending'), emoji: '🚧', colorClasses: 'bg-yellow-100 text-yellow-700' };
    // case 'waiting': 
    //   return { name: t('status.waiting', 'Waiting'), emoji: '⏳', colorClasses: 'bg-orange-100 text-orange-700' };
    default:
      // Fallback for unknown statuses
      return { name: capitalize(status), emoji: '❓', colorClasses: 'bg-gray-100 text-gray-700' };
  }
};

const MyContentHub = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Get translated tag labels
  const tagLabel = getTagLabels(t);
  

  const {
    dateRange, setDateRange,
    customDateRange, setCustomDateRange,
    selectedBrand, setSelectedBrand,
    view, setView,
    statusFilter, setStatusFilter
  } = useContentHubUrlState();

  // Compute active start/end dates from the selected date range
  const activeDateRange = useMemo(() => {
    const nowLocal = new Date();
    if (dateRange === 'custom') {
      return { start: startOfDay(customDateRange.start), end: endOfDay(customDateRange.end) };
    }
    if (dateRange === 'this-month') {
      return { start: startOfMonth(nowLocal), end: endOfDay(nowLocal) };
    }
    if (dateRange === 'last-month') {
      const lm = subMonths(nowLocal, 1);
      return { start: startOfMonth(lm), end: endOfMonth(lm) };
    }
    if (dateRange === 'this-year') {
      const yearStart = new Date(nowLocal.getFullYear(), 0, 1);
      return { start: startOfDay(yearStart), end: endOfDay(nowLocal) };
    }
    const num = Number(dateRange);
    if (Number.isFinite(num) && num > 0) {
      return { start: startOfDay(subDays(nowLocal, num)), end: endOfDay(nowLocal) };
    }
    // Fallback: last 30 days
    return { start: startOfDay(subDays(nowLocal, 30)), end: endOfDay(nowLocal) };
  }, [dateRange, customDateRange.start, customDateRange.end]);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isIdeaModalOpen, setIsIdeaModalOpen] = useState(false);
  const createPostMutation = useCreatePost();
  const [isPostFormOpen, setIsPostFormOpen] = useState(false);
  const [selectedPostForEdit, setSelectedPostForEdit] = useState<Partial<Post> | null>(null);
  const [selectedPosts, setSelectedPosts] = useState<string[]>([]);
  const [isDeletingPosts, setIsDeletingPosts] = useState(false);
  const deletionInProgress = useRef(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<PostStatus[]>([]);
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  // URL-synced sort params
  const [sortParam, setSortParam] = useUrlValue<string>('sort', 'published-date');
  const [orderParam, setOrderParam] = useUrlValue<'asc' | 'desc'>('order', 'desc');

  // State for column visibility in table
  const [columnVisibility, setColumnVisibility] = useState({
    brand: true,
    created: true,
    updated: true,
    published: true
  });
  const mapParamToSort = (p?: string): 'createdAt' | 'updatedAt' | 'publishedOn' =>
    p === 'creation-date' ? 'createdAt' : p === 'published-date' ? 'publishedOn' : 'updatedAt';
  const mapSortToParam = (c: 'createdAt' | 'updatedAt' | 'publishedOn'): 'creation-date' | 'update-date' | 'published-date' =>
    c === 'createdAt' ? 'creation-date' : c === 'publishedOn' ? 'published-date' : 'update-date';
  // Local sort state derived from URL (default: PUBLISHED desc)
  const [sortBy, setSortBy] = useState<'createdAt' | 'updatedAt' | 'publishedOn' | 'none'>(() => mapParamToSort(sortParam));
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(() => (orderParam === 'asc' ? 'asc' : 'desc'));
  useEffect(() => {
    const mapped = mapParamToSort(sortParam);
    if (mapped !== sortBy) setSortBy(mapped);
    if (orderParam !== sortDir) setSortDir(orderParam);
  }, [sortParam, orderParam]);
  
  // States for hover preview functionality
  const [hoveredPost, setHoveredPost] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const previewWrapperRef = useRef<HTMLDivElement | null>(null);
  const [previewSize, setPreviewSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [viewportSize, setViewportSize] = useState<{ width: number; height: number }>({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });
  
  const postsQuery = usePosts({
    start: activeDateRange.start,
    end: activeDateRange.end,
    applanguage: i18n.language || 'en',
    sort: sortBy === 'createdAt' ? 'creation-date' : sortBy === 'updatedAt' ? 'update-date' : sortBy === 'publishedOn' ? 'published-date' : undefined,
    order: sortDir
  });
  const { data: postsData, isLoading: isLoadingPosts, isFetching: isFetchingPosts, error: postsError } = postsQuery;
  const posts: Post[] = (postsData?.posts as Post[]) || [];

  // Render hashtags and links in blue for preview snippets
  const renderLinkedText = (text: string) => {
    const elements: React.ReactNode[] = [];
    const regex = /(https?:\/\/[^\s]+|www\.[^\s]+|#[\p{L}\d_]+)/giu;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      const start = match.index;
      if (start > lastIndex) {
        elements.push(text.slice(lastIndex, start));
      }
      const token = match[0];
      if (token.startsWith('#')) {
        elements.push(
          <span key={`hash-${start}`} className="text-[#0B65C2]">{token}</span>
        );
      } else {
        const href = token.startsWith('http') ? token : `http://${token}`;
        elements.push(
          <a key={`link-${start}`} href={href} target="_blank" rel="noreferrer" className="text-[#0B65C2] hover:underline">
            {token}
          </a>
        );
      }
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
      elements.push(text.slice(lastIndex));
    }
    return elements;
  };
  
  // Removed debug-only refresh and reset helpers
  const deletePostMutation = useDeletePost();
  const updatePostMutation = useUpdatePost();
  const bulkUpdateMutation = useBulkUpdatePosts();
  const { showToast } = useToast();

  const handlePromoteToDraft = async (post: Post) => {
    try {
      await updatePostMutation.mutateAsync({
        postId: post.id,
        postData: { status: 'draft' },
        isFormData: false
      });
      showToast(t('contentHub.promotedToDraft', 'Idea promoted to draft!'), 'success');
    } catch (error) {
      console.error('Error promoting idea to draft:', error);
      showToast(t('contentHub.promoteError', 'Failed to promote idea.'), 'error');
    }
  };

  // Marks an external (non-Socialness) post as hidden so it stops appearing
  // in My Content. Server filters hiddenByUser by default.
  const handleHidePost = async (post: Post) => {
    try {
      await updatePostMutation.mutateAsync({
        postId: post.id,
        postData: { hiddenByUser: true },
        isFormData: false
      });
      showToast(t('contentHub.hiddenExternal', 'Post masqué de votre contenu'), 'success');
    } catch (error) {
      console.error('Error hiding external post:', error);
      showToast(t('contentHub.hideError', 'Impossible de masquer le post.'), 'error');
    }
  };

  const getExternalPermalink = (post: any): string | null => {
    // External posts carry the platform permalink under platforms.<p>.results.
    // Walk every platform present on the post and take the first link.
    const platformData = post?.platformData || {};
    const platformResults = post?.platformResults || {};
    const candidates = [
      ...Object.values(platformResults).map((r: any) => r?.postUrl || r?.permalink),
      ...Object.values(platformData).map((p: any) => p?.results?.postUrl || p?.results?.permalink),
    ];
    const found = candidates.find((v) => typeof v === 'string' && v.length > 0);
    return found || null;
  };

  const statuses = useMemo(() => [
    { id: 'all', label: t('statusDropdown.all', 'All Statuses'), emoji: '🌐', description: t('statusDropdown.allDesc', 'View posts across all their current states.') },
    { id: 'idea', label: t('statusDropdown.idea', 'Idea'), emoji: '💡', description: t('statusDropdown.ideaDesc', 'A post idea saved for later with an optional reminder date.') },
    { id: 'draft', label: t('statusDropdown.draft', 'Draft'), emoji: '📝', description: t('statusDropdown.draftDesc', 'Post is in progress, not yet submitted for review or scheduling.') },
    { id: 'scheduled', label: t('statusDropdown.scheduled', 'Scheduled'), emoji: '📆', description: t('statusDropdown.scheduledDesc', 'Post is approved and set for automatic publishing at a future time.') },
    { id: 'ready_to_publish', label: t('statusDropdown.ready_to_publish', 'Ready to Publish'), emoji: '🚀', description: t('statusDropdown.ready_to_publishDesc', 'Post is complete, approved, and awaiting its scheduled time or manual publishing.') },
    { id: 'waiting_for_approval', label: t('statusDropdown.waiting_for_approval', 'Waiting for approval'), emoji: '⏳', description: t('statusDropdown.waiting_for_approvalDesc', 'Post has been submitted and is awaiting review by an approver.') },
    { id: 'published', label: t('statusDropdown.published', 'Published'), emoji: '✅', description: t('statusDropdown.publishedDesc', 'Post has been successfully shared on the selected platforms.') },
    { id: 'rejected', label: t('statusDropdown.rejected', 'Rejected'), emoji: '❌', description: t('statusDropdown.rejectedDesc', 'Post was reviewed but not approved for publishing by an approver.') },
    { id: 'error', label: t('statusDropdown.error', 'Error'), emoji: '⚠️', description: t('statusDropdown.errorDesc', 'An error occurred while trying to publish or process the post.') },
    { id: 'processing', label: t('statusDropdown.processing', 'Processing'), emoji: '⚙️', description: t('statusDropdown.processingDesc', 'Post is currently being processed by the system (e.g., media upload, API submission).') },
    { id: 'pending', label: t('statusDropdown.pending', 'Pending'), emoji: '🚧', description: t('statusDropdown.pendingDesc', 'Post is awaiting system action or external confirmation before proceeding.') },
  ], [t, i18n.language]);

  

  // Base subset for brand counts = filtered by status (multi-select aware), brand-agnostic
  const postsForBrandCounts = useMemo(() => {
    return posts.filter((post: any) => {
      // Status logic (multi-select preferred)
      if (selectedStatuses.length > 0) {
        if (!selectedStatuses.includes(post.status)) return false;
      } else if (statusFilter !== 'all' && post.status !== statusFilter) {
        return false;
      }

      // No date filtering here - the server already filtered by date range
      return true;
    });
  }, [posts, selectedStatuses, statusFilter]);

  const brandCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of postsForBrandCounts) {
      const id = (p as any).brandId || 'unknown';
      counts[id] = (counts[id] || 0) + 1;
    }
    return counts;
  }, [postsForBrandCounts]);

  // Status counts = filtered by brand selection (multi-select aware), status-agnostic
  const postsForStatusCounts = useMemo(() => {
    return posts.filter((post: any) => {
      // Brand logic (multi-select preferred)
      if (selectedBrandIds.length > 0 && !selectedBrandIds.includes('all')) {
        if (!selectedBrandIds.includes(post.brandId)) return false;
      } else if (selectedBrand !== 'all' && post.brandId !== selectedBrand) {
        return false;
      }

      // No date filtering here - the server already filtered by date range
      return true;
    });
  }, [posts, selectedBrandIds, selectedBrand]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of postsForStatusCounts) {
      const status = p.status || 'unknown';
      counts[status] = (counts[status] || 0) + 1;
    }
    return counts;
  }, [postsForStatusCounts]);

  // Platform counts = filtered by both brand and status selections
  const postsForPlatformCounts = useMemo(() => {
    return posts.filter((post: any) => {
      // Brand logic
      if (selectedBrandIds.length > 0 && !selectedBrandIds.includes('all')) {
        if (!selectedBrandIds.includes(post.brandId)) return false;
      } else if (selectedBrand !== 'all' && post.brandId !== selectedBrand) {
        return false;
      }

      // Status logic
      if (selectedStatuses.length > 0) {
        if (!selectedStatuses.includes(post.status)) return false;
      } else if (statusFilter !== 'all' && post.status !== statusFilter) {
        return false;
      }

      return true;
    });
  }, [posts, selectedBrandIds, selectedBrand, selectedStatuses, statusFilter]);

  const platformCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of postsForPlatformCounts) {
      const platformsArr = getPlatformsArray(p);
      for (const plat of platformsArr) {
        counts[plat] = (counts[plat] || 0) + 1;
      }
    }
    return counts;
  }, [postsForPlatformCounts]);

  // Sorting handlers and styles
  const handleSort = (col: 'createdAt' | 'updatedAt' | 'publishedOn') => {
    if (sortBy === col) {
      const next = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(next);
      setOrderParam(next);
    } else {
      setSortBy(col);
      setSortParam(mapSortToParam(col));
      setSortDir('desc');
      setOrderParam('desc');
    }
  };
  const sortBtnClass = (col: 'createdAt' | 'updatedAt' | 'publishedOn') =>
    `inline-flex items-center gap-1 ${sortBy === col ? 'text-primary-700 font-semibold' : ''} hover:text-primary-600`;

  // When opening the drawer, initialize local multi-selects from current single-selects
  useEffect(() => {
    if (!isFiltersOpen) return;
    // Brands: if nothing picked in multi-select, mirror the single-select (including 'all')
    if (selectedBrandIds.length === 0) {
      setSelectedBrandIds(selectedBrand !== 'all' ? [selectedBrand] : ['all']);
    }
    // Statuses: if none picked, mirror single-select (but we do not expose 'all' as a checkbox)
    if (selectedStatuses.length === 0 && statusFilter !== 'all') {
      setSelectedStatuses([statusFilter]);
    }
  }, [isFiltersOpen]);

  useEffect(() => {
    document.title = t('contentHub.title', 'My Content Hub');
  }, [t]);

  useEffect(() => {
    if (view !== 'list' && view !== 'list-simplified' && view !== 'grid' && view !== 'board') {
      setView('list-simplified');
    }
  }, [view]);

  // Observe the real size of the preview and update on the fly
  useEffect(() => {
    if (!previewWrapperRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        setPreviewSize({ width: cr.width, height: cr.height });
      }
    });
    observer.observe(previewWrapperRef.current);
    return () => observer.disconnect();
  }, [hoveredPost]);

  // Track viewport changes so the position recomputes while open
  useEffect(() => {
    const handleResize = () => setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Removed visibilitychange-based refresh to avoid unnecessary API calls on tab focus

  if (isLoadingPosts) {
    // Skeleton only when not actively calling API (or initial layout before data)
    return (
      <PageLayout title={t('contentHub.title', 'My Content Hub')} subtitle={t('contentHub.subtitle', 'Manage all your content in one place')} actions={
        <div className="flex items-center gap-3">
          <div className="h-10 bg-gray-200 rounded-lg w-32" />
        </div>
      }>
        <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                    <th key={i} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 w-4 bg-gray-200 rounded"></div></td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-gray-200 rounded mr-4"></div>
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                       <div className="flex gap-1">
                          <div className="h-4 w-4 bg-gray-200 rounded-full"></div>
                          <div className="h-4 w-4 bg-gray-200 rounded-full"></div>
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell"><div className="h-6 w-24 bg-gray-200 rounded"></div></td>
                    <td className="px-4 py-4 whitespace-nowrap"><div className="h-5 w-16 bg-gray-200 rounded-full"></div></td>
                    <td className="px-3 py-4 whitespace-nowrap"><div className="h-4 w-20 bg-gray-200 rounded"></div></td>
                    <td className="px-6 py-4 whitespace-nowrap hidden lg:table-cell"><div className="h-4 w-20 bg-gray-200 rounded"></div></td>
                    <td className="px-6 py-4 whitespace-nowrap text-right"><div className="h-5 w-5 bg-gray-200 rounded"></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </PageLayout>
    );
  }
  if (postsError) {
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-4 lg:pt-0">
        <div className="w-[95%] mx-auto py-8 flex items-center justify-center h-96">
          <p className="text-red-600">Failed to load posts. Please try again later.</p>
        </div>
      </div>
    );
  }

  const brands: Brand[] = [
    { id: 'all', name: t('brandDropdown.allBrands', 'All Brands'), logo: '/logo.svg' },
    ...Array.from(
      new Map(posts.map((post: any) => [post.brandId, { 
        id: post.brandId || 'unknown', 
        name: post.brandName || 'Unknown Brand', 
        logo: post.brandLogo || '/logo.svg'
      }])).values()
    ) as Brand[]
  ];

  const filteredPosts = posts.filter((post: any) => {
    // Brand filter: use multi-select if set, otherwise fallback to single select
    if (selectedBrandIds.length > 0 && !selectedBrandIds.includes('all')) {
      if (!selectedBrandIds.includes(post.brandId)) return false;
    } else if (selectedBrand !== 'all' && post.brandId !== selectedBrand) {
      return false;
    }

    // Status filter: use multi-select if set, otherwise fallback to single select
    if (selectedStatuses.length > 0) {
      if (!selectedStatuses.includes(post.status)) return false;
    } else if (statusFilter !== 'all' && post.status !== statusFilter) {
      return false;
    }

    // Platform filter (multi-select)
    if (selectedPlatforms.length > 0) {
      const postPlatforms = getPlatformsArray(post);
      if (!postPlatforms.some((p: string) => selectedPlatforms.includes(p))) return false;
    }
    return true;
  });


  const sortedPosts = [...filteredPosts].sort((a, b) => {
    const getMs = (post: any) => {
      let d: string | undefined;
      if (sortBy === 'createdAt') d = post.createdAt;
      else if (sortBy === 'updatedAt') d = post.updatedAt;
      else if (sortBy === 'publishedOn') d = getPublishedOnDate(post as any);
      else d = getDisplayDate(post as any);
      if (!d) return -Infinity;
      try {
        const p = parseISO(d);
        return isValid(p) ? p.getTime() : -Infinity;
      } catch { return -Infinity; }
    };

    const msA = getMs(a);
    const msB = getMs(b);
    if (sortDir === 'asc') return msA - msB;
    return msB - msA;
  });

  const now = new Date();
  const nextScheduledPost = sortedPosts.find((post: any) => {
    const sched = (post as any).scheduledFor;
    return sched && typeof sched === 'string' && sched.trim() && isValid(parseISO(sched)) && isAfter(parseISO(sched), now);
  });

  const handlePostClick = (post: Post) => {
    // External posts are read-only mirrors of what the user published directly
    // on the platform — open them on the platform rather than the editor.
    if (post.source === 'external') {
      const permalink = getExternalPermalink(post);
      if (permalink) {
        window.open(permalink, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    // Pass the full post to avoid refetching on CreateEditPostPage when possible
    navigate(`/posts/${post.id}/edit`, { state: { post } });
  };

  const handleSelectPost = (postId: string, e: React.MouseEvent | React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setSelectedPosts((prev) => prev.includes(postId) ? prev.filter((id) => id !== postId) : [...prev, postId]);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPosts(e.target.checked ? filteredPosts.map(post => post.id) : []);
  };

  const handleBulkDelete = async () => {
    if (selectedPosts.length === 0 || deletionInProgress.current) return;
    deletionInProgress.current = true;
    setIsDeletingPosts(true);
    const postIds = [...new Set(selectedPosts)];

    try {
      const results = await Promise.allSettled(postIds.map(postId => deletePostMutation.mutateAsync(postId)));
      const deletedIds = new Set(postIds.filter((_, index) => results[index].status === 'fulfilled'));
      const failures = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected');
      logger.info('MyContentHub', `Deleted ${deletedIds.size} posts; ${failures.length} failed`);
      // Leave failed posts selected, including when other deletions succeeded.
      setSelectedPosts(current => current.filter(postId => !deletedIds.has(postId)));
      setHoveredPost(current => current && deletedIds.has(current) ? null : current);
      if (failures.length > 0) {
        const messages = [...new Set(failures.map(result => getPostDeleteErrorMessage(result.reason, t)))].join('\n');
        const summary = deletedIds.size > 0
          ? t('postDeleteErrors.partial', {
              deleted: deletedIds.size,
              failed: failures.length,
              defaultValue: '{{deleted}} posts deleted; {{failed}} could not be deleted.',
            }) + '\n'
          : '';
        alert(summary + messages);
      }
    } catch (error) {
      logger.error('MyContentHub', 'Error deleting posts', error);
      alert(getPostDeleteErrorMessage(error, t));
    } finally {
      deletionInProgress.current = false;
      setIsDeletingPosts(false);
    }
  };

  const handleBulkEdit = async (updateData: BulkUpdateData) => {
    if (selectedPosts.length === 0) return;

    try {
      const result = await bulkUpdateMutation.mutateAsync({
        postIds: selectedPosts,
        updateData: {
          status: updateData.status,
          brandId: updateData.brandId,
          platforms: updateData.platforms as string[]
        }
      });

      logger.info('MyContentHub', `Bulk edit completed: ${result.updated} updated, ${result.failed.length} failed`);
      
      if (result.success) {
        setSelectedPosts([]);
        setShowBulkEditModal(false);
        
        if (result.failed.length > 0) {
          alert(`${result.updated} posts updated successfully. ${result.failed.length} posts failed to update.`);
        } else {
          alert(`All ${result.updated} posts updated successfully!`);
        }
      } else {
        alert(`Failed to update posts. ${result.errors.length} errors occurred.`);
      }
    } catch (error) {
      logger.error('MyContentHub', 'Error in bulk edit', error);
      alert('Failed to update posts. Please try again.');
    }
  };

  const handleDeletePost = (postId: string) => {
    if (deletionInProgress.current) return;
    setSelectedPosts([postId]);
    setShowDeleteModal(true);
    setHoveredPost(null); // Hide preview when modal opens and ensure it's cleared
  };

  const handleDuplicatePost = (post: Post) => {
    const contentCopy = `[COPY] ${post.content || ''}`;
    const normalizedMedia = normalizeMedia(post.media);
    // Get first platform from the platforms object
    const firstPlatform = getPlatformsArray(post)[0] || null;
    
    navigate('/posts/new', {
      state: {
        content: contentCopy,
        platform: firstPlatform,
        brandId: (post as any).brandId || (post as any).brand_id || undefined,
        imageUrl: normalizedMedia ? normalizedMedia.url : null
      }
    });
  };

  const handleCheckStatus = async () => {
    try {
      setStatusMessage('Checking status...');
      setStatusModalOpen(true);
    } catch (error) {
      logger.error('MyContentHub', 'Failed to check post status', error);
      setStatusMessage('Error checking status');
      setStatusModalOpen(true);
    }
  };

  // Hover handlers for preview
  const handleRowMouseEnter = (e: React.MouseEvent, postId: string) => {
    setHoveredPost(postId);
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleRowMouseMove = (e: React.MouseEvent) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleRowMouseLeave = () => {
    setHoveredPost(null);
  };

  const getOptimalPosition = () => {
    const margin = 16; // Margin from screen edges
    const rawWidth = previewSize.width || Math.min(800, viewportSize.width * 0.9);
    const rawHeight = previewSize.height || Math.min(500, viewportSize.height * 0.8);

    const maxWidth = Math.max(0, viewportSize.width - margin * 2);
    const maxHeight = Math.max(0, viewportSize.height - margin * 2);
    const scale = Math.min(1, maxWidth / rawWidth, maxHeight / rawHeight);

    const width = rawWidth * scale;
    const height = rawHeight * scale;

    // Prefer right side of cursor, fallback to left if overflow
    let x = mousePosition.x + 12;
    if (x + width + margin > viewportSize.width) {
      x = mousePosition.x - width - 12;
    }

    // Start vertically near the cursor, then clamp
    let y = mousePosition.y - height * 0.3;

    // Clamp to viewport so it is fully visible
    x = Math.min(Math.max(margin, x), viewportSize.width - margin - width);
    y = Math.min(Math.max(margin, y), viewportSize.height - margin - height);

    return { x, y, scale };
  };

  const handleStatusModalClose = () => {
    setStatusModalOpen(false);
  };

  const getDeleteConfirmationMessage = () => {
    if (selectedPosts.length === 1) {
      const post = posts.find((p: Post) => p.id === selectedPosts[0]);
      return `Are you sure you want to delete the post "${truncateContent(post?.content || '', 30)}"? This action cannot be undone.`;
    }
    return `Are you sure you want to delete ${selectedPosts.length} selected posts? This action cannot be undone.`;
  };

  const handleCreateWithAI = () => {
    logger.info('MyContentHub', 'Creating post with AI');
    setIsCreateModalOpen(false);
    navigate('/content-creation');
  };

  const handleCreateManually = () => {
    setIsCreateModalOpen(false);
    navigate('/posts/new');
  };

  const handleCreateIdea = () => {
    setIsCreateModalOpen(false);
    setIsIdeaModalOpen(true);
  };

  const handlePostSubmit = async (_data: Partial<Post> & { publishNow?: boolean }) => {
    logger.info('MyContentHub', 'Post submitted in Content Hub (needs implementation)');
    setIsPostFormOpen(false);
    setSelectedPostForEdit(null);
  };

  return (
    <>
      {(isFetchingPosts) && (
        <GifLoader
          title={t('contentHub.loading.title', 'Loading your content...')}
          currentStep={t('contentHub.loading.refreshing', 'Refreshing posts...')}
          videoSrc="/Panda - Gold Digger.mov"
        />
      )}
      <PageLayout
        title={t('contentHub.title', 'My Content Hub')}
        subtitle={t('contentHub.subtitle', 'Manage all your content in one place')}
        actions={
          <div className="flex items-center gap-3">
                {selectedPosts.length > 0 && (
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setShowBulkEditModal(true)}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Edit3 className="w-5 h-5 mr-2" />
                      Edit {selectedPosts.length} {selectedPosts.length === 1 ? 'Post' : 'Posts'}
                    </button>
                    <button 
                      disabled={isDeletingPosts}
                      onClick={() => {
                        setShowDeleteModal(true);
                        setHoveredPost(null); // Hide preview when modal opens
                      }}
                      className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-5 h-5 mr-2" />
                      Delete {selectedPosts.length} {selectedPosts.length === 1 ? 'Post' : 'Posts'}
                    </button>
                  </div>
                )}
            {/* Debug buttons removed */}
            <button 
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('sidebar.createContent')}
            </button>
          </div>
        }
      >

            {/* Filters and Controls */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              {/* Quick status shortcuts (left) */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setSelectedStatuses([]); setStatusFilter('all'); }}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg ${statusFilter === 'all' && selectedStatuses.length === 0 ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  title={t('statusDropdown.allDesc', 'View posts across all their current states.')}
                >
                  {t('statusDropdown.all', 'All')}
                </button>
                <button
                  onClick={() => { setSelectedStatuses([]); setStatusFilter('idea' as PostStatus); }}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg ${statusFilter === 'idea' && selectedStatuses.length === 0 ? 'bg-amber-100 text-amber-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  title={t('statusDropdown.ideaDesc', 'A post idea saved for later with an optional reminder date.')}
                >
                  {t('statusDropdown.idea', 'Ideas')}
                </button>
                <button
                  onClick={() => { setSelectedStatuses([]); setStatusFilter('draft'); }}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg ${statusFilter === 'draft' && selectedStatuses.length === 0 ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  title={t('statusDropdown.draftDesc', 'Post is in progress, not yet submitted for review or scheduling.')}
                >
                  {t('statusDropdown.draft', 'Drafts')}
                </button>
                <button
                  onClick={() => { setSelectedStatuses([]); setStatusFilter('scheduled'); }}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg ${statusFilter === 'scheduled' && selectedStatuses.length === 0 ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  title={t('statusDropdown.scheduledDesc', 'Post is approved and set for automatic publishing at a future time.')}
                >
                  {t('statusDropdown.scheduled', 'Scheduled')}
                </button>
                <button
                  onClick={() => { setSelectedStatuses([]); setStatusFilter('published'); }}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg ${statusFilter === 'published' && selectedStatuses.length === 0 ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  title={t('statusDropdown.publishedDesc', 'Post has been successfully shared on the selected platforms.')}
                >
                  {t('statusDropdown.published', 'Published')}
                </button>
              </div>

              {/* Right controls: View toggles, Filters */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto sm:ml-auto">
                {/* View toggles first */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setView('list-simplified')}
                    className={`group relative inline-flex items-center px-3 py-1.5 rounded-lg ${view === 'list-simplified' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                    aria-label={t('contentHub.view.list_simplified', 'List simplified')}
                  >
                    <List className="w-4 h-4" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                      {t('contentHub.view.list_simplified', 'List simplified')}
                    </div>
                  </button>
                  <button
                    onClick={() => setView('list')}
                    className={`group relative inline-flex items-center px-3 py-1.5 rounded-lg ${view === 'list' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                    aria-label={t('contentHub.view.list', 'List')}
                  >
                    <ListPlus className="w-4 h-4" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                      {t('contentHub.view.list', 'List')}
                    </div>
                  </button>
                  <button
                    onClick={() => setView('grid')}
                    className={`group relative inline-flex items-center px-3 py-1.5 rounded-lg ${view === 'grid' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                    aria-label={t('contentHub.view.grid', 'Grid')}
                  >
                    <Grid className="w-4 h-4" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                      {t('contentHub.view.grid', 'Grid')}
                    </div>
                  </button>
                  <button
                    onClick={() => setView('board')}
                    className={`group relative inline-flex items-center px-3 py-1.5 rounded-lg ${view === 'board' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                    aria-label={t('contentHub.view.board', 'Board')}
                  >
                    <LayoutGrid className="w-4 h-4" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                      {t('contentHub.view.board', 'Board')}
                    </div>
                  </button>
                </div>
                {/* Filters button */}
                {(() => {
                  const activeFiltersCount =
                    (selectedStatuses.length || (statusFilter !== 'all' ? 1 : 0)) +
                    (selectedBrandIds.length || (selectedBrand !== 'all' ? 1 : 0)) +
                    (selectedPlatforms.length || 0);
                  return (
                    <div className="relative">
                      <button
                        onClick={() => setIsFiltersOpen((v) => !v)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:text-primary-600"
                        aria-expanded={isFiltersOpen}
                      >
                        <Filter className="w-4 h-4 mr-2 text-primary-500" />
                        {t('contentHub.filters', 'Filtres')}
                        {activeFiltersCount > 0 && (
                          <span className="ml-2 inline-flex items-center justify-center rounded-full bg-primary-600 text-white text-xs w-5 h-5">
                            {activeFiltersCount}
                          </span>
                        )}
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>
        {/* Content Area */}
        <div className="w-full mx-auto py-2">
          <div className={view === 'board' ? '' : ''}>
          {view === 'list' || view === 'list-simplified' ? (
              <div className="bg-white rounded-xl border border-black shadow-lg overflow-hidden">
                {selectedPosts.length > 0 && (
                  <div className="px-6 py-3 bg-primary-50 border-b border-primary-100 flex items-center justify-between">
                     <div className="flex items-center gap-2">
                      <span className="text-primary-700 font-medium">
                        {selectedPosts.length} {selectedPosts.length === 1 ? 'post' : 'posts'} selected
                      </span>
                      <button onClick={() => setSelectedPosts([])} className="text-primary-600 text-sm hover:text-primary-800 ml-2">
                        Clear selection
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setShowBulkEditModal(true)} className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
                        <Edit3 className="w-4 h-4 mr-1" /> Edit {selectedPosts.length} posts
                      </button>
                      <button disabled={isDeletingPosts} onClick={() => setShowDeleteModal(true)} className="inline-flex items-center px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        <Trash2 className="w-4 h-4 mr-1" /> Delete selected
                      </button>
                    </div>
                  </div>
                )}
                
                {nextScheduledPost && (
                    <div className="px-6 py-3 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-blue-700">
                        Next scheduled post: {formatLocaleDate((nextScheduledPost as any)?.scheduledFor, i18n.language)}
                      </span>
                    </div>
                 )}
                {filteredPosts.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                         <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                            <input type="checkbox" checked={selectedPosts.length > 0 && selectedPosts.length === filteredPosts.length} onChange={handleSelectAll} className="h-4 w-4 text-primary-600 border-gray-300 rounded cursor-pointer" title="Select all posts" />
                          </th>
                          <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('contentHub.table.content', 'Content')}</th>
                          <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">{t('contentHub.table.platforms', 'Platforms')}</th>
                          {columnVisibility.brand && (
                            <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">{t('contentHub.table.brand', 'Brand')}</th>
                          )}
                          <th scope="col" className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('contentHub.table.status', 'Status')}</th>
                          <th scope="col" className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('contentHub.table.stats', 'Stats')}</th>
                          {columnVisibility.created && view !== 'list-simplified' && (
                            <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                              <button
                                onClick={() => handleSort('createdAt')}
                                className={sortBtnClass('createdAt')}
                              >
                                {t('contentHub.table.created', 'CREATED')}
                                <span className="text-[10px]">{sortBy === 'createdAt' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</span>
                              </button>
                            </th>
                          )}
                          {columnVisibility.updated && view !== 'list-simplified' && (
                            <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                              <button
                                onClick={() => handleSort('updatedAt')}
                                className={sortBtnClass('updatedAt')}
                              >
                                {t('contentHub.table.updated', 'UPDATED')}
                                <span className="text-[10px]">{sortBy === 'updatedAt' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</span>
                              </button>
                            </th>
                          )}
                          {columnVisibility.published && (
                            <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                              <button
                                onClick={() => handleSort('publishedOn')}
                                className={sortBtnClass('publishedOn')}
                              >
                                {t('contentHub.table.publishedOn', 'PUBLISHED')}
                                <span className="text-[10px]">{sortBy === 'publishedOn' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</span>
                              </button>
                            </th>
                          )}
                          <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('contentHub.table.actions', 'Actions')}</th>
                      </tr>
                    </thead>
                     <tbody className="bg-white divide-y divide-gray-200">
                        {sortedPosts.map((post: any) => {
                          const isNext = nextScheduledPost?.id === post.id;
                          const currentUserId = auth.currentUser?.uid;
                          const { name: statusName, emoji: statusEmoji, colorClasses: statusColorClasses } = getStatusDisplayInfo(post, currentUserId, t);
                          // Check for processing status
                          const hasProcessingStatus =
                            post.status === 'processing' ||
                            Object.values(post.platforms || {}).some((platformData: any) => 
                              platformData?.results?.status === 'processing'
                            );
                          return (
                            <tr 
                              key={post.id}
                              onClick={() => handlePostClick(post)}
                              className={`hover:bg-gray-50 cursor-pointer ${isNext ? 'bg-blue-50' : ''} ${selectedPosts.includes(post.id) ? 'bg-primary-50' : ''}`}
                            >
                               <td className="px-3 sm:px-6 py-2 sm:py-4" onClick={(e) => e.stopPropagation()}>
                                <input type="checkbox" checked={selectedPosts.includes(post.id)} onChange={(e) => handleSelectPost(post.id, e)} className="h-4 w-4 text-primary-600 border-gray-300 rounded cursor-pointer" />
                              </td>
                              <td 
                                className="px-3 sm:px-6 py-2 sm:py-4"
                                onMouseEnter={(e) => handleRowMouseEnter(e, post.id)}
                                onMouseMove={(e) => handleRowMouseMove(e)}
                                onMouseLeave={handleRowMouseLeave}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                                    {(() => {
                                      const normalizedMedia = normalizeMedia(post.media);
                                      if (!normalizedMedia) return null;
                                      const isPdf = /\.pdf(\?|#|$)/i.test(normalizedMedia.url);
                                      return (
                                        <div className="w-14 h-14 sm:w-14 sm:h-14 rounded bg-gray-100 overflow-hidden">
                                          {isPdf ? (
                                            <PdfThumb src={normalizedMedia.url} className="w-full h-full" frame="square" />
                                          ) : (
                                            <MediaDisplay
                                              mediaUrl={normalizedMedia.url}
                                              altText="Post media"
                                              type={normalizedMedia.type}
                                              objectFit="cover"
                                              controls={false}
                                              autoPlayOnMount={normalizedMedia.type === 'video'}
                                              muted
                                            />
                                          )}
                                        </div>
                                      );
                                    })()}
                                    {/* Brand logo under image on mobile */}
                                    {post.brandLogo && (
                                      <div className="sm:hidden">
                                        <img 
                                          src={post.brandLogo} 
                                          alt={post.brandName || 'Brand'} 
                                          className="w-4 h-3 rounded-md object-contain"
                                        />
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p
                                      className="text-xs sm:text-sm text-gray-900"
                                      style={{ display: '-webkit-box', WebkitLineClamp: 2 as any, WebkitBoxOrient: 'vertical' as any, overflow: 'hidden' }}
                                    >
                                      {post.status === 'idea'
                                        ? renderLinkedText(truncateContent(post.title || post.content || '', 140))
                                        : renderLinkedText(truncateContent(post.content, 140))}
                                    </p>
                                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                      {post.source === 'external' && (
                                        <span
                                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border bg-gray-100 text-gray-600 border-gray-200"
                                          title={t('contentHub.externalPostTooltip', 'Cette publication a été faite directement sur la plateforme, sans passer par Socialness.')}
                                        >
                                          <span>🔗</span>
                                          <span>{t('contentHub.externalBadge', 'Publié hors Socialness')}</span>
                                        </span>
                                      )}
                                      {(() => {
                                        const fv = (post.format === 'reel' ? 'reel' : 'regular') as 'reel' | 'regular';
                                        const fs = tagStyles[fv];
                                        return (
                                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${fs.bg} ${fs.text} ${fs.border}`}>
                                            <span>{tagEmoji[fv]}</span>
                                            <span>{tagLabel[fv]}</span>
                                          </span>
                                        );
                                      })()}
                                      {(() => {
                                        // Extremely simple mapping: image | video | document | multi-image
                                        const raw = ((post as any).postMediaType || '').toString().toLowerCase();
                                        let variant: import('../utils/tagStyles').TagVariant;
                                        if (raw) {
                                          if (raw.includes('multi')) variant = 'multi';
                                          else if (raw === 'document' || raw === 'pdf' || raw === 'file') variant = 'document';
                                          else if (raw === 'video') variant = 'video';
                                          else if (raw === 'image') variant = 'image';
                                          else variant = 'text';
                                        } else {
                                          const m = normalizeMedia(post.media);
                                          variant = getMediaTagVariant(m?.url, m?.type);
                                        }
                                        const s = tagStyles[variant];
                                        return (
                                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${s.bg} ${s.text} ${s.border}`}>
                                            <span>{tagEmoji[variant]}</span>
                                            <span>{tagLabel[variant]}</span>
                                          </span>
                                        );
                                      })()}
                                    </div>
                                    
                                    <div className="flex items-center gap-1 mt-1 sm:hidden">
                                      {getPlatformsArray(post).map((platform: string) => <SocialIcon key={platform} platform={platform as any} size={12}/>)}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 sm:px-6 py-2 sm:py-4 hidden sm:table-cell">
                                <div className="flex gap-1">
                                  {getPlatformsArray(post).map((platform: string) => <SocialIcon key={platform} platform={platform as any} size={16}/>)}
                                </div>
                              </td>
                              
                              {columnVisibility.brand && (
                                <td className="px-3 sm:px-6 py-2 sm:py-4 hidden md:table-cell">
                                  {post.brandLogo ? (
                                    <div className="flex items-center">
                                      <img src={post.brandLogo} alt={post.brandName || 'Brand'} className="w-8 h-6 rounded-lg object-contain"/>
                                    </div>
                                  ) : <span className="text-sm text-gray-500">No brand</span>}
                                </td>
                              )}
                              
                              <td className="px-3 sm:px-4 py-2 sm:py-4">
                                <div className="relative group inline-block">
                                  {getPlatformsArray(post).length > 1 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {getPlatformsArray(post).map((platform: string) => {
                                        const platformStatus = getPlatformStatus(post, platform);
                                        const { name: statusName, emoji: statusEmoji, colorClasses: statusColorClasses } = getStatusDisplayInfo({ ...post, status: platformStatus }, auth.currentUser?.uid, t);
                                        return (
                                          <span key={platform} className={`inline-flex items-center gap-1 px-1.5 sm:px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColorClasses}`}
                                            title={statuses.find(s => s.id === platformStatus)?.description || ''}
                                          >
                                            <SocialIcon platform={platform as any} size={14} /> 
                                            <span>{statusEmoji}</span>
                                            <span className="hidden sm:inline ml-1">{statusName}</span>
                                          </span>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <span className={`inline-flex items-center gap-1 px-1.5 sm:px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColorClasses}`}>
                                      <span>{statusEmoji}</span>
                                      <span className="hidden sm:inline ml-1">{statusName}</span>
                                    </span>
                                  )}
                                  <div className="absolute left-1/2 z-20 -translate-x-1/2 mt-2 w-56 bg-black text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 shadow-lg whitespace-normal">
                                    {getPlatformsArray(post).length > 1
                                      ? getPlatformsArray(post).map((platform: string) => {
                                          const platformStatus = getPlatformStatus(post, platform);
                                          return (
                                            <div key={platform} className="flex items-center gap-1 mb-1 last:mb-0">
                                              <SocialIcon platform={platform as any} size={12} />
                                              <span>{statuses.find(s => s.id === platformStatus)?.description || ''}</span>
                                            </div>
                                          );
                                        })
                                      : (statuses.find(s => s.id === post.status)?.description || '')}
                                  </div>
                                </div>
                              </td>
                              
                              <td 
                                className="px-2 sm:px-3 py-2 sm:py-4 text-sm text-gray-500"
                              >
                                {post.status === 'published' ? (
                                  <div className="flex flex-col gap-1">
                                    {/* Row 1 (mobile & desktop): Likes / Comments / Views */}
                                    <div className="flex items-center gap-2 sm:gap-4 text-xs">
                                      <span className="group relative inline-flex items-center gap-1">
                                        <span className="text-red-500">❤️</span>
                                        {post.engagement?.likes || 0}
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                          {t('analytics.metrics.likes','Likes')}
                                        </div>
                                      </span>
                                      <span className="group relative inline-flex items-center gap-1">
                                        <span className="text-blue-500">💬</span>
                                        {post.engagement?.comments || 0}
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                          {t('analytics.metrics.comments','Comments')}
                                        </div>
                                      </span>
                                      <span className="group relative inline-flex items-center gap-1">
                                        <span className="text-purple-500">👁️</span>
                                        {post.engagement?.views || 0}
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                          {t('analytics.metrics.views','Views')}
                                        </div>
                                      </span>
                                    </div>
                                    {/* Row 2 (mobile only): Shares / Clicks / Unique views */}
                                    <div className="flex items-center gap-2 text-xs sm:hidden">
                                      <span className="group relative inline-flex items-center gap-1">
                                        <span className="text-green-500">🔗</span>
                                        {post.engagement?.shares || 0}
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                          {t('analytics.metrics.shares','Shares')}
                                        </div>
                                      </span>
                                      <span className="group relative inline-flex items-center gap-1">
                                        <span>👉</span>
                                        {post.engagement?.clicks || 0}
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                          {t('analytics.metrics.clicks','Clicks')}
                                        </div>
                                      </span>
                                      <span className="group relative inline-flex items-center gap-1">
                                        <span>🔍</span>
                                        {post.engagement?.unique_views || post.engagement?.uniqueViews || post.engagement?.uniqueImpressions || 0}
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                          {t('analytics.metrics.uniqueViews','Unique Views')}
                                        </div>
                                      </span>
                                    </div>
                                    {/* Row 2 (desktop): Shares / Clicks / Unique views */}
                                    <div className="hidden sm:flex items-center gap-4 text-xs">
                                      <span className="group relative inline-flex items-center gap-1">
                                        <span className="text-green-500">🔗</span>
                                        {post.engagement?.shares || 0}
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                          {t('analytics.metrics.shares','Shares')}
                                        </div>
                                      </span>
                                      <span className="group relative inline-flex items-center gap-1">
                                        <span>👉</span>
                                        {post.engagement?.clicks || 0}
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                          {t('analytics.metrics.clicks','Clicks')}
                                        </div>
                                      </span>
                                      <span className="group relative inline-flex items-center gap-1">
                                        <span>🔍</span>
                                        {post.engagement?.unique_views || post.engagement?.uniqueViews || post.engagement?.uniqueImpressions || 0}
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                          {t('analytics.metrics.uniqueViews','Unique Views')}
                                        </div>
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-gray-400 text-xs">{t('contentHub.noStatsYet', 'No stats yet')}</span>
                                )}
                              </td>
                              
                              {columnVisibility.created && view !== 'list-simplified' && (
                                <td className="px-3 sm:px-6 py-2 sm:py-4 text-sm text-gray-500 hidden lg:table-cell">
                                  {post.createdAt ? formatLocaleDate(post.createdAt, i18n.language) : '-'}
                                </td>
                              )}
                              {columnVisibility.updated && view !== 'list-simplified' && (
                                <td className="px-3 sm:px-6 py-2 sm:py-4 text-sm text-gray-500 hidden lg:table-cell">
                                  {post.updatedAt ? formatLocaleDate(post.updatedAt, i18n.language) : '-'}
                                </td>
                              )}
                              {columnVisibility.published && (
                                <td className="px-3 sm:px-6 py-2 sm:py-4 text-sm text-gray-500 hidden lg:table-cell">
                                  {(() => {
                                    const d = getPublishedOnDate(post);
                                    return d ? formatLocaleDate(d, i18n.language) : '-';
                                  })()}
                                </td>
                              )}
                              
                              <td className="px-3 sm:px-6 py-2 sm:py-4 text-right">
                                  <ActionsMenu
                                    onModify={() => handlePostClick(post)}
                                    onDelete={() => handleDeletePost(post.id)}
                                    onDuplicate={() => handleDuplicatePost(post)}
                                    onCheckStatus={hasProcessingStatus ? () => handleCheckStatus() : undefined}
                                    onPromoteToDraft={post.status === 'idea' ? () => handlePromoteToDraft(post) : undefined}
                                    status={post.status}
                                    menuPosition="top"
                                    isAnyProcessing={hasProcessingStatus}
                                    isExternal={post.source === 'external'}
                                    externalPermalink={post.source === 'external' ? getExternalPermalink(post) : null}
                                    onHide={post.source === 'external' ? () => handleHidePost(post) : undefined}
                                  />

                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                  </table>
                </div>
              ) : (
                 <NoPostsCard 
                    title="No posts found"
                    message="You haven't created any posts yet. Get started by creating your first post!"
                    icon="list"
                    isFiltered={selectedBrand !== 'all' || statusFilter !== 'all'}
                  />
              )}
            </div>
          ) : view === 'grid' ? (
            <>
             {filteredPosts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {filteredPosts.map((post: any) => (
                    <div 
                      key={post.id} 
                      onClick={() => handlePostClick(post)}
                      className="cursor-pointer transform transition-transform hover:scale-[1.02]"
                    >
                      <SocialMediaCard
                        post={post}
                        brandLogo={post.brandLogo}
                        username={post.brandName ? post.brandName.toLowerCase().replace(/\s/g, '') : ''}
                        brandName={post.brandName}
                        onDelete={handleDeletePost}
                        onHide={(postId) => {
                          const target = filteredPosts.find((p: any) => p.id === postId);
                          if (target) handleHidePost(target as Post);
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <NoPostsCard 
                  title="Your content gallery is empty"
                  message="Time to showcase your creativity! Create your first post and watch your social media presence grow."
                  icon="image"
                  isFiltered={selectedBrand !== 'all' || statusFilter !== 'all'}
                />
              )}
            </>
          ) : view === 'board' ? (
            <BoardView posts={filteredPosts} onPostClick={handlePostClick} />
          ) : (
             <p>Unknown view selected</p>
          )}
          </div>
        </div>
      </PageLayout>

      {/* Right-side sliding Filters drawer */}
      {isFiltersOpen && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setIsFiltersOpen(false)}
          />
          {/* Panel */}
          <div className="absolute right-0 top-0 h-full w-full sm:w-[380px] bg-white shadow-2xl border-l border-gray-200 transform transition-transform duration-300 ease-out">
            <div className="h-full flex flex-col">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">{t('contentHub.filters', 'Filtres')}</h3>
                <button
                  onClick={() => setIsFiltersOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
                {/* Date range group - placed on top */}
                <div>
                  <div className="text-sm font-semibold text-gray-900 mb-3">{t('contentHub.filter.dateRange', 'Période')}</div>
                  <DateRangePicker
                    dateRange={dateRange}
                    customDateRange={customDateRange}
                    onDateRangeChange={setDateRange}
                    onCustomDateRangeChange={setCustomDateRange}
                    fullWidth
                  />
                </div>
                {/* Status group */}
                <div>
                  <div className="text-sm font-semibold text-gray-900 mb-3">{t('contentHub.filter.status', 'Statut')}</div>
                  <div className="space-y-3">
                    {statuses.filter(s => s.id !== 'all').map(s => (
                      <div key={s.id} className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1 rounded border-gray-300 text-primary-600"
                          checked={selectedStatuses.includes(s.id as PostStatus)}
                          onChange={(e) => {
                            setSelectedStatuses(prev => e.target.checked ? [...prev, s.id as PostStatus] : prev.filter(v => v !== (s.id as PostStatus)));
                          }}
                        />
                        <div className="min-w-0">
                          <div className="text-sm text-gray-900 flex items-center gap-2">
                            <span className="shrink-0">{s.emoji}</span>
                            <span className="truncate font-semibold">{s.label} ({statusCounts[s.id] ?? 0})</span>
                          </div>
                          <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                            {(statuses.find(ss => ss.id === s.id)?.description) || ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Brands group */}
                <div>
                  <div className="text-sm font-semibold text-gray-900 mb-3">{t('contentHub.filter.brands', 'Marques')}</div>
                  <div className="space-y-3">
                    {brands.map(b => (
                      <label key={b.id} className="flex items-center gap-3 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary-600"
                          checked={selectedBrandIds.includes(b.id)}
                          onChange={(e) => {
                            if (b.id === 'all') {
                              setSelectedBrandIds(e.target.checked ? ['all'] : []);
                              return;
                            }
                            setSelectedBrandIds(prev => {
                              const withoutAll = prev.filter(id => id !== 'all');
                              if (e.target.checked) {
                                if (withoutAll.includes(b.id)) return withoutAll;
                                return [...withoutAll, b.id];
                              }
                              return withoutAll.filter(v => v !== b.id);
                            });
                          }}
                        />
                        <span className="inline-flex items-center gap-2 min-w-0">
                          {b.id !== 'all' && b.logo ? (
                            <img src={b.logo} alt={b.name} className="w-5 h-5 rounded object-contain shrink-0" />
                          ) : null}
                          <span className="truncate max-w-[180px]" title={b.name}>{b.name}</span>
                          {b.id !== 'all' && (
                            <span className="shrink-0 text-gray-500">({brandCounts[b.id] ?? 0})</span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Platforms group */}
                <div>
                  <div className="text-sm font-semibold text-gray-900 mb-3">{t('contentHub.filter.platforms', 'Plateformes')}</div>
                  <div className="space-y-3">
                    {['instagram','linkedin','facebook','tiktok','youtube','twitter','pinterest','google_business']
                      .filter(p => (platformCounts[p] ?? 0) > 0)
                      .map(p => (
                      <label key={p} className="flex items-center gap-3 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary-600"
                          checked={selectedPlatforms.includes(p)}
                          onChange={(e) => {
                            setSelectedPlatforms(prev => e.target.checked ? [...prev, p] : prev.filter(v => v !== p));
                          }}
                        />
                        <span className="inline-flex items-center gap-2">
                          <SocialIcon platform={p as any} size={14} />
                          <span className="truncate capitalize max-w-[220px]">
                            {p.replace('_',' ')} ({platformCounts[p] ?? 0})
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Columns group */}
                <div>
                  <div className="text-sm font-semibold text-gray-900 mb-3">{t('contentHub.filter.columns', 'Colonnes')}</div>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-600"
                        checked={columnVisibility.brand}
                        onChange={(e) => setColumnVisibility(prev => ({ ...prev, brand: e.target.checked }))}
                      />
                      <span className="truncate max-w-[220px]">
                        {t('contentHub.columns.brand', 'Marque')}
                      </span>
                    </label>
                    <label className="flex items-center gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-600"
                        checked={columnVisibility.created}
                        onChange={(e) => setColumnVisibility(prev => ({ ...prev, created: e.target.checked }))}
                      />
                      <span className="truncate max-w-[220px]">
                        {t('contentHub.columns.created', 'Créé')}
                      </span>
                    </label>
                    <label className="flex items-center gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-600"
                        checked={columnVisibility.updated}
                        onChange={(e) => setColumnVisibility(prev => ({ ...prev, updated: e.target.checked }))}
                      />
                      <span className="truncate max-w-[220px]">
                        {t('contentHub.columns.updated', 'Modifié')}
                      </span>
                    </label>
                    <label className="flex items-center gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-600"
                        checked={columnVisibility.published}
                        onChange={(e) => setColumnVisibility(prev => ({ ...prev, published: e.target.checked }))}
                      />
                      <span className="truncate max-w-[220px]">
                        {t('contentHub.columns.published', 'Publié')}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedStatuses([]);
                    setSelectedBrandIds([]);
                    setStatusFilter('all');
                    setSelectedBrand('all');
                    setSelectedPlatforms([]);
                    setColumnVisibility({
                      brand: true,
                      created: true,
                      updated: true,
                      published: true
                    });
                  }}
                  className="px-4 py-2 text-sm rounded-lg text-gray-700 hover:text-primary-600"
                >
                  {t('common.reset', 'Réinitialiser')}
                </button>
                <div />
              </div>
            </div>
          </div>
        </div>
      )}

      <CreatePostModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateWithAI={handleCreateWithAI}
        onCreateManually={handleCreateManually}
        onCreateIdea={handleCreateIdea}
      />
      <IdeaModal
        isOpen={isIdeaModalOpen}
        onClose={() => setIsIdeaModalOpen(false)}
        isLoading={createPostMutation.isPending}
        onSave={async (idea) => {
          try {
            await createPostMutation.mutateAsync({
              postData: {
                title: idea.title,
                description: idea.description,
                brandId: idea.brandId,
                status: 'idea',
                reminderAt: idea.reminderAt,
                platforms: {},
                content: '',
              }
            });
            showToast(t('calendar.ideaSaved', 'Idea saved!'), 'success');
            setIsIdeaModalOpen(false);
          } catch (error) {
            console.error('Error saving idea:', error);
            showToast(t('calendar.ideaError', 'Failed to save idea.'), 'error');
          }
        }}
      />
      <PostForm
        isOpen={isPostFormOpen}
        onClose={() => { setIsPostFormOpen(false); setSelectedPostForEdit(null); }}
        onSubmit={handlePostSubmit}
        initialData={selectedPostForEdit}
      />
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleBulkDelete}
        title={`Delete ${selectedPosts.length === 1 ? 'Post' : 'Selected Posts'}`}
        message={getDeleteConfirmationMessage()}
        warning="All associated data and media files will be permanently deleted."
      />
      <StatusModal
        isOpen={statusModalOpen}
        onClose={handleStatusModalClose}
        status={statusMessage}
        autoCloseDelay={4000}
      />
      <BulkEditModal
        isOpen={showBulkEditModal}
        onClose={() => setShowBulkEditModal(false)}
        onConfirm={handleBulkEdit}
        selectedPostsCount={selectedPosts.length}
        brands={brands}
        isLoading={bulkUpdateMutation.isPending}
      />

      {/* Hover Preview Tooltip for List View */}
      {view === 'list' && hoveredPost && !showDeleteModal && !showBulkEditModal && !statusModalOpen && (() => {
        const hoveredPostData = sortedPosts.find((p: any) => p.id === hoveredPost);
        return hoveredPostData ? (
          <div 
            ref={previewWrapperRef}
            className="fixed pointer-events-none z-50"
            style={{
              left: getOptimalPosition().x,
              top: getOptimalPosition().y,
              transform: `scale(${getOptimalPosition().scale})`,
              transformOrigin: 'top left',
            }}
          >
            <PostPreview post={hoveredPostData} />
          </div>
        ) : null;
      })()}
    </>
  );
};

export default MyContentHub;
